import { statusIsSet } from './cost.js';
import { isSharedPerson } from './decisions.js';
/* ---------- prototype-pollution guards (orgchart.html:2904-2910) ---------- */
/** Keys that reach Object.prototype when used as a dynamic property name. */
export const FORBIDDEN_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
export function isForbiddenKey(k) {
    return FORBIDDEN_KEYS.has(k);
}
/** JSON.parse reviver: drop prototype-polluting keys anywhere in a parsed project file. */
export function scrubKeys(key, value) {
    return isForbiddenKey(key) ? undefined : value;
}
export function safeJsonParse(text) {
    return JSON.parse(text, scrubKeys);
}
/* ---------- import limits (orgchart.html:2913-2937) ---------- */
export const MAX_IMPORT_BYTES = 100 * 1024 * 1024;
export const MAX_IMPORT_ROWS = 200_000;
export class ProjectFileError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ProjectFileError';
    }
}
/**
 * Reassemble the chunked payload and parse it.
 *
 * Chunks are ordered by their numeric suffix rather than by row order: a spreadsheet round trip
 * through another tool can reorder rows, and concatenating them wrongly yields invalid JSON that
 * looks like a corrupt file rather than a sorting bug.
 */
export function decodeProjectMeta(rows) {
    const chunks = [];
    let schemaVersion = '';
    let app = '';
    for (const row of rows) {
        const key = String(row?.key ?? '');
        const value = row?.value == null ? '' : String(row.value);
        if (key === '__schema_version__')
            schemaVersion = value;
        else if (key === '__app__')
            app = value;
        else {
            const m = /^__payload__(\d+)$/.exec(key);
            if (m)
                chunks.push({ index: Number(m[1]), value });
        }
    }
    if (!chunks.length)
        throw new ProjectFileError('No project payload found in the metadata sheet');
    chunks.sort((a, b) => a.index - b.index);
    for (let i = 0; i < chunks.length; i++) {
        if (chunks[i].index !== i) {
            throw new ProjectFileError(`Project payload is missing chunk ${i}`);
        }
    }
    let meta;
    try {
        meta = safeJsonParse(chunks.map((c) => c.value).join(''));
    }
    catch (err) {
        throw new ProjectFileError(`Project payload is not valid JSON: ${err.message}`);
    }
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
        throw new ProjectFileError('Project payload is not an object');
    }
    return { schemaVersion, app, meta: meta };
}
/* ---------- legacy migrations (orgchart.html:9300, :9448) ---------- */
/** Pre-generic-hierarchy files keyed levels by name. Mirrors migrateLegacyMeta(). */
const LEGACY_LEVEL_KEYS = ['board', 'function', 'subFunction'];
export function migrateLegacyLevelKeys(meta) {
    const levels = meta.groupingLevels;
    if (Array.isArray(levels) && levels.length)
        return meta;
    const columnMap = (meta.columnMap ?? {});
    const present = LEGACY_LEVEL_KEYS.filter((k) => columnMap[k]);
    if (!present.length)
        return meta;
    const migrated = [];
    const nextColumnMap = { ...columnMap };
    present.forEach((legacyKey, i) => {
        const key = `lvl${i + 1}`;
        migrated.push({ key, label: legacyKey === 'subFunction' ? 'Sub-function' : legacyKey.replace(/^./, (c) => c.toUpperCase()) });
        nextColumnMap[key] = columnMap[legacyKey];
        delete nextColumnMap[legacyKey];
    });
    return { ...meta, groupingLevels: migrated, columnMap: nextColumnMap };
}
/** v8 moved `phase` from a number to a string id. Old 2 was Perimeter, never Target. */
const LEGACY_PHASES = {
    '1': 'data',
    '2': 'perimeter',
    '3': 'transfers',
    '4': 'tsa',
    '5': 'remainco',
};
export function migratePhase(phase) {
    if (typeof phase === 'number')
        return LEGACY_PHASES[String(phase)] ?? 'data';
    if (typeof phase === 'string' && phase)
        return LEGACY_PHASES[phase] ?? phase;
    return 'data';
}
/**
 * Dedication, ported verbatim from parseDedication() (orgchart.html:1868).
 *
 * Accepts ratios ("0.6"), percent strings ("60%") and bare percent numbers (>1 reads as percent).
 * The single `,`→`.` replacement is the original's, kept deliberately rather than routed through
 * this module's fuller `parseNumber`: dedication cells never carry thousands separators, and the
 * parity harness holds this to the shipped app's exact output.
 */
export function parseDedication(value) {
    if (value == null || String(value).trim() === '')
        return { dedication: null, legacyMarker: false };
    const s = String(value).trim();
    const num = Number(s.replace('%', '').replace(',', '.'));
    if (!Number.isFinite(num))
        return { dedication: null, legacyMarker: statusIsSet(s) };
    let d = s.includes('%') || num > 1 ? num / 100 : num;
    d = Math.max(0, Math.min(1, d));
    return { dedication: d, legacyMarker: false };
}
/**
 * Whether a marker column on an IMPORTED row is set: any non-blank cell counts.
 *
 * Ported from isOpenPosVal() (orgchart.html:1831). Deliberately NOT the same test as statusIsSet(),
 * and deliberately not a boolean-word test either. HR extracts write whatever the client's HRIS
 * writes into an open-position column — "Vacant", "TBH", "Open", "V", a requisition number, a target
 * start date — so on import the signal is *presence*, and the wording is not interpreted. Reading
 * these as booleans (accepting only yes/true/1/x) silently marks every vacancy as a filled post,
 * which then counts in headcount, FTE and cost: an overstated organisation, with no error anywhere.
 *
 * statusIsSet() is the other half of the pair and applies to a value a USER later types into the
 * field, where "Filled" / "No" must be able to clear it. Two predicates, two jobs.
 */
function markerSet(value) {
    if (typeof value === 'boolean')
        return value;
    return value != null && String(value).trim() !== '';
}
/**
 * Parse a number out of a spreadsheet cell, coping with both separator conventions.
 *
 * HR extracts arrive from every locale, so "120,000" (thousands) and "0,5" (decimal comma) both
 * turn up — and getting this wrong is silent: a salary off by a factor of 1000 flows straight into
 * the cost model. The rule is positional rather than locale-guessing: whichever separator appears
 * last is the decimal point, and a lone separator followed by exactly three digits in a grouped
 * pattern is thousands.
 */
export function parseNumber(value) {
    if (value == null || value === '')
        return null;
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    let s = String(value).trim().replace(/\s/g, '');
    if (!s)
        return null;
    const negative = /^\(.*\)$/.test(s) || s.startsWith('-'); // (1,234) is accounting for -1234
    s = s.replace(/[()]/g, '').replace(/[^0-9.,]/g, '');
    if (!s)
        return null;
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma >= 0 && lastDot >= 0) {
        // Both present: the rightmost is the decimal separator, the other is grouping.
        const decimalAt = Math.max(lastComma, lastDot);
        const groupChar = decimalAt === lastComma ? '.' : ',';
        s = s.split(groupChar).join('');
        s = s.replace(/,/g, '.');
    }
    else if (lastComma >= 0) {
        s = /^\d{1,3}(,\d{3})+$/.test(s) ? s.replace(/,/g, '') : s.replace(/,/g, '.');
    }
    else if (lastDot >= 0) {
        if (/^\d{1,3}(\.\d{3})+$/.test(s))
            s = s.replace(/\./g, '');
    }
    const n = Number(s);
    if (!Number.isFinite(n))
        return null;
    return negative ? -Math.abs(n) : n;
}
function numberOrNull(value) {
    return parseNumber(value);
}
/**
 * The name exactly as the shipped app derives it: a blank cell stays blank, and only a MISSING name
 * column produces the literal '(unnamed)'.
 *
 * Both halves matter. '(unnamed)' is load-bearing rather than cosmetic — a vacancy with no name falls
 * back to its job title on the card precisely by testing for it (orgchart.html:4377) — so inventing a
 * different placeholder here, or substituting the personnel id, would relabel every open position with
 * a reference number on the chart and in the client deck.
 */
function nameFromRow(value) {
    return value == null ? '(unnamed)' : String(value);
}
function stringOrNull(value) {
    if (value == null)
        return null;
    const s = String(value).trim();
    return s === '' ? null : s;
}
export function employeesFromRows(rows, options) {
    const { columnMap, groupingLevels } = options;
    const cell = (row, key) => {
        const column = columnMap[key];
        return column ? row[column] : undefined;
    };
    const out = [];
    const seen = new Set();
    rows.forEach((row, i) => {
        const id = stringOrNull(cell(row, 'id')) ?? `row-${i + 1}`;
        if (isForbiddenKey(id))
            return; // never let a crafted id become an object key
        if (seen.has(id))
            return; // first occurrence wins, as the original does
        seen.add(id);
        const levels = {};
        for (const level of groupingLevels) {
            levels[level.key] = stringOrNull(cell(row, level.key));
        }
        // Dedication, with the legacy fallback: when no numeric dedication column is mapped but the old
        // boolean split-head column is, that column supplies the marker (orgchart.html:3573-3575). Such a
        // person is known to be shared by an UNKNOWN amount, so dedication stays null and they carry no
        // stranded capacity — see parseDedication.
        const ded = columnMap.dedication
            ? parseDedication(cell(row, 'dedication'))
            : { dedication: null, legacyMarker: columnMap.splitHead ? markerSet(cell(row, 'splitHead')) : false };
        // Open takes precedence over inactive, matching empFromRow(): a vacant post is not "inactive".
        const openPosition = markerSet(cell(row, 'openPosition'));
        out.push({
            id,
            name: nameFromRow(cell(row, 'name')),
            supervisorId: stringOrNull(cell(row, 'supervisorId')),
            jobTitle: stringOrNull(cell(row, 'jobTitle')),
            location: stringOrNull(cell(row, 'location')),
            country: stringOrNull(cell(row, 'country')),
            currency: stringOrNull(cell(row, 'currency')),
            pensionScheme: stringOrNull(cell(row, 'pensionScheme')),
            seniority: stringOrNull(cell(row, 'seniority')),
            businessUnit: stringOrNull(cell(row, 'businessUnit')),
            legalEntity: stringOrNull(cell(row, 'legalEntity')),
            salary: numberOrNull(cell(row, 'salary')),
            fte: numberOrNull(cell(row, 'fte')),
            dedication: ded.dedication,
            sharedMarker: isSharedPerson(ded.dedication, ded.legacyMarker),
            openPosition,
            inactive: markerSet(cell(row, 'inactive')) && !openPosition,
            isLeaver: options.leavers?.has(id) ?? false,
            isBuildUp: false,
            levels,
            raw: options.keepRaw ? row : null,
        });
    });
    return out;
}
/** Grouping levels as declared by the project file, with the shape defended. */
export function groupingLevelsFromMeta(meta) {
    const levels = meta.groupingLevels;
    if (!Array.isArray(levels))
        return [];
    return levels
        .filter((l) => !!l && typeof l === 'object')
        .map((l, i) => ({
        key: String(l.key ?? `lvl${i + 1}`),
        label: String(l.label ?? `Level ${i + 1}`),
    }))
        .filter((l) => /^[a-z]+\d+$/i.test(l.key) && !isForbiddenKey(l.key));
}
//# sourceMappingURL=project-file.js.map